import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, Spinner } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { GetEventJudgeStore } from 'SessionState/Judge/EventJudgeStore';
import LayoutJudgePortal from 'layout/LayoutJudgePortal';
import JudgeNavLogo from 'layout/nav/JudgeNavLogo';
import { localScoreRepository } from 'Repositories/Local/localScoreRepository';
import JudgeTabulatorHelpMessage from './JudgeTabulatorHelpMessage';

const JudgeLogin = observer(() => {
  const title = 'Judge Login';
  const description = 'Judge Portal Login';
  const navigate = useHistory();

  const [showHelpModal, setShowHelpModal] = useState(false);
const [helpName, setHelpName] = useState('');
const [helpPageantName, setHelpPageantName] = useState('');
const [helpMessage, setHelpMessage] = useState('');
const [isSendingHelp, setIsSendingHelp] = useState(false);



  useEffect(() => {
    let manifestLink: HTMLLinkElement | null =
      document.querySelector('link[rel="manifest"]');

    if (!manifestLink) {
      manifestLink = document.createElement('link');
      manifestLink.rel = 'manifest';
      document.head.appendChild(manifestLink);
    }

    manifestLink.href = '/judge/manifest.json';
    document.title = 'HelloTiara Judges';
  }, []);

  const [loginError, setLoginError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    accessCode: Yup.string()
      .trim()
      .min(4, 'Code is too short')
      .required('Judge code is required'),
  });

const onSubmit = async (values: { accessCode: string }) => {
  try {
    setFormLoading(true);
    setLoginError(null);

    const normalizedCode = values.accessCode.trim().toUpperCase();
    const judgeStore = GetEventJudgeStore();

    const previousJudgeRaw = sessionStorage.getItem('currentJudge');
    const previousJudge = previousJudgeRaw
      ? JSON.parse(previousJudgeRaw)
      : null;

    const result = await judgeStore.loginWithAccessCode(normalizedCode);

    if (!result) {
      setLoginError('Invalid code or access expired.');
      return;
    }

    const isDifferentJudge =
      previousJudge &&
      (Number(previousJudge.id) !== Number(result.id) ||
        Number(previousJudge.eventId) !== Number(result.eventId));

    if (isDifferentJudge) {
      await localScoreRepository.deleteJudgePortalSnapshot(
        `${previousJudge.id}:${previousJudge.eventId}`,
      );
    }

    await localScoreRepository.saveJudgeSession({
      id: `${result.eventId}:${result.id}`,
      eventJudgeId: Number(result.id),
      eventId: Number(result.eventId),
      pageantId: Number((result as any).pageantId ?? 0),
      judgeName: String(
        (result as any).judgeName ?? (result as any).name ?? '',
      ),
      judgeLabel: String((result as any).judgeLabel ?? ''),
      accessCode: normalizedCode.trim().toLowerCase(),
      cachedAt: Date.now(),
    });

    navigate.push(`/judge/eventhome/${result.id}/${result.eventId}`);
  } catch (error) {
    setLoginError('Invalid code or access expired.');
  } finally {
    setFormLoading(false);
  }
};

  const formik = useFormik({
    initialValues: {
      accessCode: '',
    },
    validationSchema,
    onSubmit,
  });

  const { handleSubmit, handleChange, values, touched, errors } = formik;

  const Loading = ({ isLoading }: { isLoading: boolean }) => {
    if (!isLoading) return null;

    return (
      <Spinner
        as="span"
        animation="border"
        size="sm"
        className="me-2"
      />
    );
  };

  const content = (
    <div
      className="bg-foreground d-flex justify-content-center align-items-center overflow-hidden"
      style={{ minHeight: 'calc(90vh - var(--nav-size-slim))' }}
    >
      <div className="sw-lg-50 px-5 text-center">
        <div className="mb-5 d-flex justify-content-center">
          <JudgeNavLogo />
        </div>

        <div className="mb-4">
          <h1 className="display-3 fw-bold text-primary">
            Ready to Judge?
          </h1>
        </div>

        <div className="mb-5">
          <p className="h6">
            Enter the code provided by the event director.
          </p>
        </div>

        <form
          id="judgeLoginForm"
          className="tooltip-end-bottom"
          onSubmit={handleSubmit}
        >
          <div className="mb-3 filled form-group tooltip-end-top">
            <CsLineIcons icon="key" />

            <Form.Control
              type="text"
              name="accessCode"
              placeholder="Judge Code"
              value={values.accessCode}
              onChange={handleChange}
              autoComplete="off"
            />

            {errors.accessCode && touched.accessCode && (
              <div className="d-block invalid-tooltip">
                {errors.accessCode}
              </div>
            )}
          </div>

          <div className="d-flex justify-content-center">
            <Button size="lg" type="submit" disabled={formLoading}>
              <Loading isLoading={formLoading} />
              <span>Enter Portal</span>
            </Button>
          </div>

          {loginError && (
            <div className="mt-3 text-center text-danger">
              {loginError}
            </div>
          )}
        </form>

<div className="mt-5 mb-5 text-center">
  <p className="h6 mb-2">Need help logging in?</p>
  <p className="text-medium text-muted mb-3">
    Text the admin if you need your judge code or your code is not working.
  </p>

  <Button
    variant="outline-primary"
    onClick={() => {
      setHelpMessage('');
      setShowHelpModal(true);
    }}
  >
    Message for Help
  </Button>
</div>

        <div className="text-center text-muted w-100">
          <a aria-current="page" href="/">
            <em>Leave Judges Portal and Go To HelloTiara.com</em>
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <HtmlHead title={title} description={description} image="" />
      <LayoutJudgePortal>{content}</LayoutJudgePortal>
      <JudgeTabulatorHelpMessage
  show={showHelpModal}
  onHide={() => setShowHelpModal(false)}
  mode="login-help"
  initialMessage="I need help with my judge code. I either need my code or my code is not working."
/>
    </>
  );
});

export default JudgeLogin;